import React from "react";
import { Box, Typography } from "@mui/joy";
import { Download, RotateCw, FileDown, Eye } from "lucide-react";
import { DocumentPreviewWidget, ContractPreview } from "../../";
import { useDocument, useAsset, useInspectionAttributes, useMeasurement, useCart, useReport } from "../../../hooks/query";
import useProjectQuery from "../../../hooks/query/useProject";
import { useNotification } from "../../../hooks/useNotification";
import { useProject } from "../../../hooks";
import { useContractSigning } from "./hooks";

export function ContractReview() {
    const { project } = useProject();
    const { contractDocument, workOrderDocument, purchaseOrderDocument, hasGutterWork, refreshContract } = useContractSigning();

    const { useGenerateContract, useWorkOrderCreate, useSupplyOrderCreate } = useDocument();
    const { useGeneratePreCannedReport } = useReport();
    const { useDownload } = useAsset();
    const { showSuccess, showError } = useNotification();

    const generateContractSvc = useGenerateContract();
    const generateWorkOrderSvc = useWorkOrderCreate();
    const generateSupplyOrderSvc = useSupplyOrderCreate();
    const generateGutterReportSvc = useGeneratePreCannedReport();
    const downloadAssetSvc = useDownload();
    
    const { useListByProjectId: useListAttributes } = useInspectionAttributes();
    const { useListByProjectId: useListMeasurements } = useMeasurement();
    const { useListByProjectId: useListCart } = useCart();
    const { useListById: useGetProject } = useProjectQuery();

    const listAttributesSvc = useListAttributes();
    const listMeasurementsSvc = useListMeasurements();
    const listCartSvc = useListCart();
    const getProjectSvc = useGetProject();

    const [previewOpen, setPreviewOpen] = React.useState(false);
    const [previewData, setPreviewData] = React.useState<any>(null);

    const handleOpenPreview = async () => {
        if (!project?.id) return;
        try {
            const [attributes, measurements, cartItems, projectResponse] = await Promise.all([
                listAttributesSvc.mutateAsync({ projectId: project.id }),
                listMeasurementsSvc.mutateAsync({ projectId: project.id }),
                listCartSvc.mutateAsync({ ProjectId: project.id }),
                getProjectSvc.mutateAsync({ Id: project.id })
            ]);

            const fullProject = Array.isArray(projectResponse) ? projectResponse[0] : projectResponse;

            if (!fullProject) throw new Error("Project details not found");

            const safeAttributes = Array.isArray(attributes) ? attributes : [];
            const safeMeasurements = Array.isArray(measurements) ? measurements : [];
            const safeCartItems = Array.isArray(cartItems) ? cartItems : [];

            const getA = (name: string) => safeAttributes.find((a: any) => a.AttributeName === name)?.AttributeValue || "N/A";
            const getM = (type: string) => {
                const m = safeMeasurements.find((m: any) => m.MeasurementType === type);
                return m ? String(m.ActualValue || m.CalculatedValue || "0") : "0";
            };

            const roofMaterial = safeCartItems.find((i: any) => i.ProductCategory === "Field Shingles")?.ProductName || "N/A";

            setPreviewData({
                Header: {
                    ReferenceNumber: String(fullProject.Id || "0000").padStart(6, '0'),
                    Date: new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }),
                },
                OwnerInfo: {
                    Name: fullProject.CustomerName || "N/A",
                    SalesRep: fullProject.SalesmanName || "N/A",
                    Address: fullProject.CustomerStreet || "N/A",
                    City: fullProject.CustomerCity || "N/A",
                    State: fullProject.CustomerState || "N/A",
                    Zip: fullProject.CustomerZipCode || "N/A",
                    Phone: fullProject.CustomerPhoneNumber || "N/A",
                    Email: fullProject.CustomerEmail || "N/A",
                    InsuranceCompany: fullProject.InsuranceProvider || "N/A",
                    ClaimNumber: fullProject.InsuranceClaimNumber || "N/A",
                    Date: new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }),
                },
                RoofingScope: {
                    Material: roofMaterial,
                    Color: getA("Roof Color"),
                    Pitch: getA("Roof Pitch"),
                    Layers: getA("Tear Off Layer"),
                    EaveDripEdge: getA("Eave Drip Edge"),
                    RakeDripEdge: getA("Rake Drip Edge"),
                    ValleyType: getA("Valley Type"),
                },
                Pricing: {
                    RoofPrice: "$0.00", // Would require frontend calc logic or backend estimate
                    GutterPrice: "$0.00",
                    TotalPrice: "$0.00",
                    EstimatedStartDate: fullProject.StartDate ? new Date(fullProject.StartDate).toLocaleDateString() : "TBD",
                },
                Notes: getA("Special Project Notes"),
            });
            setPreviewOpen(true);
        } catch (e) {
            console.error(e);
            showError("Failed to load project details for preview");
        }
    };

    const handleRegenerateContract = async () => {
        try {
            if (!project?.id) {
                showError("Project ID not found");
                return;
            }
            await generateContractSvc.mutateAsync({ ProjectId: project.id });
            showSuccess("Contract regenerated successfully");
            setTimeout(() => {
                refreshContract();
            }, 1000);
        } catch (error) {
            console.error("Error regenerating contract:", error);
            showError("Failed to regenerate contract");
        }
    };

    const handleGenerateWorkOrder = async () => {
        try {
            if (!project?.id) {
                showError("Project ID not found");
                return;
            }
            await generateWorkOrderSvc.mutateAsync({ ProjectId: project.id });
            showSuccess("Work Order generated successfully");
            setTimeout(() => {
                refreshContract();
            }, 1000);
        } catch (error) {
            console.error("Error generating work order:", error);
            showError("Failed to generate work order");
        }
    };

    const handleGeneratePurchaseOrder = async () => {
        try {
            if (!project?.id) {
                showError("Project ID not found");
                return;
            }
            await generateSupplyOrderSvc.mutateAsync({ ProjectId: project.id });
            showSuccess("Purchase Order generated successfully");
            setTimeout(() => {
                refreshContract();
            }, 1000);
        } catch (error) {
            console.error("Error generating purchase order:", error);
            showError("Failed to generate purchase order");
        }
    };

    const handleGenerateGutterReport = async () => {
        try {
            if (!project?.id) return;
            await generateGutterReportSvc.mutateAsync({ ReportType: "GUTTER_REPORT", ProjectId: project.id });
            showSuccess("Gutter Report downloaded successfully");
        } catch (error) {
            console.error("Error generating gutter report:", error);
            showError("Failed to generate gutter report");
        }
    };

    return (
        <Box mt={4} display="flex" flexDirection="column" gap={4}>
            {/* Contract Section */}
            <Box>
                <Typography level={"body-sm"} sx={{ color: "text.primary", fontWeight: 600 }} gutterBottom>
                    Documents Review
                </Typography>
                <Typography level={"body-md"} sx={{ color: "text.primary" }}>
                    Formal agreement to define project deliverables, timelines, and obligations.
                </Typography>
                <Box mt={2} display={"flex"} gap={2}>
                    <DocumentPreviewWidget
                        thumbnailUrl={contractDocument?.thumbnailUrl}
                        title="Contract"
                        description="Project agreement and terms"
                        status={contractDocument?.Status || "NOT GENERATED"}
                        assetId={contractDocument?.DocumentAssetId}
                        buttonText={contractDocument ? "Download" : "Generate Contract"}
                        buttonIcon={<FileDown size={16} />}
                        onButtonClick={handleOpenPreview}
                        loading={generateContractSvc.isPending || listAttributesSvc.isPending || getProjectSvc.isPending}
                        menuActions={
                            contractDocument
                                ? [
                                      {
                                          label: "Regenerate",
                                          icon: <RotateCw size={16} />,
                                          onClick: handleOpenPreview,
                                          disabled: generateContractSvc.isPending,
                                      },
                                      {
                                          label: "Preview",
                                          icon: <Eye size={16} />,
                                          onClick: handleOpenPreview,
                                      }
                                  ]
                                : []
                        }
                    />
                    <DocumentPreviewWidget
                        thumbnailUrl={workOrderDocument?.thumbnailUrl}
                        title="Work Order"
                        description="Outlines the scope and materials needed for the crew."
                        status={workOrderDocument?.Status || "NOT GENERATED"}
                        assetId={workOrderDocument?.DocumentAssetId}
                        buttonText={workOrderDocument ? "Download" : "Generate Work Order"}
                        buttonIcon={<FileDown size={16} />}
                        onButtonClick={!workOrderDocument ? handleGenerateWorkOrder : undefined}
                        loading={generateWorkOrderSvc.isPending}
                        menuActions={
                            workOrderDocument
                                ? [
                                      {
                                          label: "Regenerate",
                                          icon: <RotateCw size={16} />,
                                          onClick: handleGenerateWorkOrder,
                                          disabled: generateWorkOrderSvc.isPending,
                                      },
                                  ]
                                : []
                        }
                    />
                    <DocumentPreviewWidget
                        thumbnailUrl={purchaseOrderDocument?.thumbnailUrl}
                        title="Purchase Order"
                        description="Material supply order request for standard supplier procurement."
                        status={purchaseOrderDocument?.Status || "NOT GENERATED"}
                        assetId={purchaseOrderDocument?.DocumentAssetId}
                        buttonText={purchaseOrderDocument ? "Download" : "Generate Purchase Order"}
                        buttonIcon={<FileDown size={16} />}
                        onButtonClick={!purchaseOrderDocument ? handleGeneratePurchaseOrder : undefined}
                        loading={generateSupplyOrderSvc.isPending}
                        menuActions={
                            purchaseOrderDocument
                                ? [
                                      {
                                          label: "Regenerate",
                                          icon: <RotateCw size={16} />,
                                          onClick: handleGeneratePurchaseOrder,
                                          disabled: generateSupplyOrderSvc.isPending,
                                      },
                                  ]
                                : []
                        }
                    />
                    {hasGutterWork && (
                        <DocumentPreviewWidget
                            thumbnailUrl={""}
                            title="Gutter Work Order"
                            description="Gutter Work Order & Invoice Report."
                            status={"GENERATED"}
                            buttonText={"Download Report"}
                            buttonIcon={<FileDown size={16} />}
                            onButtonClick={handleGenerateGutterReport}
                            loading={generateGutterReportSvc.isPending}
                        />
                    )}
                </Box>
            </Box>

            <ContractPreview 
                open={previewOpen}
                onClose={() => setPreviewOpen(false)}
                data={previewData}
                isGenerating={generateContractSvc.isPending}
                onGenerate={async () => {
                    await handleRegenerateContract();
                    setPreviewOpen(false);
                }}
            />
        </Box>
    );
}
